/**
 * FinancialRecordsHub — iter-237.6 (Feb 2026)
 *
 * "السجل والمتابعة المالية للشركة" — 4 records-only sub-cards:
 *   1) سجل الرواتب              → /finance/asb606/payments/salaries?mode=records
 *   2) سجل مدفوعات الشركة       → /finance/asb606/payments/company?mode=records
 *   3) سجل أصول الشركة          → /finance/asb606/payments/assets?mode=records
 *   4) سجل مستحقات الشركة       → /finance/asb606/payments/liabilities?mode=records
 *
 * The sub-pages read `?mode=records` and hide their add-buttons / entry
 * forms so the operator sees only the historical ledger.
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, Briefcase, Gem, HandCoins,
  ArrowLeft, ArrowRight,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { usePerms } from '../../permissions/PermissionsContext';

export default function FinancialRecordsHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const CARDS = [
    {
      id: 'salaries',
      icon: Users,
      color: '#10B981',
      route: '/finance/asb606/payments/salaries?mode=records',
      titleAr: 'سجل الرواتب',
      titleEn: 'Salary Records',
      descAr: 'عرض ومتابعة كل رواتب الموظفين المسجّلة بالفلاتر والإجماليات',
      descEn: 'Full ledger of employee salaries with filters and totals',
      testid: 'records-hub-salaries',
    },
    {
      id: 'payments',
      icon: Briefcase,
      color: '#3B82F6',
      route: '/finance/asb606/payments/company?mode=records',
      titleAr: 'سجل مدفوعات الشركة',
      titleEn: 'Company Payments Records',
      descAr: 'عرض ومتابعة فواتير الاشتراكات والخدمات مع بحث وفلاتر',
      descEn: 'Full ledger of subscription/service invoices',
      testid: 'records-hub-payments',
    },
    {
      id: 'assets',
      icon: Gem,
      color: '#d4af37',
      route: '/finance/asb606/payments/assets?mode=records',
      titleAr: 'سجل أصول الشركة',
      titleEn: 'Assets Records',
      descAr: 'عرض المعادن، الأسهم، والعقارات المسجّلة مع الإجماليات',
      descEn: 'Metals, stocks, and real estate with grand totals',
      testid: 'records-hub-assets',
    },
    {
      id: 'liabilities',
      icon: HandCoins,
      color: '#EF4444',
      route: '/finance/asb606/payments/liabilities?mode=records',
      titleAr: 'سجل مستحقات الشركة',
      titleEn: 'Liabilities Records',
      descAr: 'قائمة المستحقات (مدفوع/جزئي/غير مدفوع) وسجل التسديدات',
      descEn: 'Liabilities (paid/partial/unpaid) with payments log',
      testid: 'records-hub-liabilities',
    },
  ];

  return (
    <DashboardLayout
      title="السجل والمتابعة المالية"
      titleEn="Financial Records & Tracking"
      subtitle="راجع كل البيانات المسجّلة (رواتب، مدفوعات، أصول، مستحقات)"
      subtitleEn="Review all saved data (salaries, payments, assets, liabilities)"
      screenCode="ASB-FIN-PAY-REC"
      backPath="/finance/asb606"
      onBack={() => navigate('/finance/asb606')}
      breadcrumb={[
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'ASB606 (المالية)', labelEn: 'ASB606 (Finance)', path: '/finance/asb606' },
        { labelAr: 'السجل والمتابعة', labelEn: 'Records & Tracking' },
      ]}
    >
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
        data-testid="financial-records-hub"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {visiblePages(CARDS, 'route').map((card, i) => {
            const Icon = card.icon;
            const Arrow = isAr ? ArrowLeft : ArrowRight;
            return (
              <motion.button
                key={card.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -4, scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => navigate(card.route)}
                className="group relative bg-white/[0.02] backdrop-blur-xl border rounded-2xl p-6 text-start transition-all overflow-hidden"
                style={{ borderColor: `${card.color}30` }}
                data-testid={card.testid}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 shrink-0"
                    style={{ backgroundColor: `${card.color}15`, borderColor: `${card.color}40` }}
                  >
                    <Icon className="h-7 w-7" style={{ color: card.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                      {isAr ? card.titleAr : card.titleEn}
                      <Arrow className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: card.color }} />
                    </h3>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed">
                      {isAr ? card.descAr : card.descEn}
                    </p>
                  </div>
                </div>
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ background: `radial-gradient(circle at 50% 50%, ${card.color}10 0%, transparent 70%)` }}
                />
              </motion.button>
            );
          })}
        </div>
      </section>
    </DashboardLayout>
  );
}
